import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Eye, Heart, ImagePlus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { fetchPostPreviewFn } from "@/lib/social.functions";
import { showcasePostsQuery, formatCount, type ShowcasePost } from "@/lib/people";
import { SOCIAL_LABELS } from "@/components/socials";
import { useT } from "@/i18n";

export function ShowcasePostsPanel({ userId }: { userId: string | undefined }) {
  const { t } = useT();
  const queryClient = useQueryClient();
  const preview = useServerFn(fetchPostPreviewFn);
  const { data: posts } = useQuery(showcasePostsQuery(userId));
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ["showcase-posts", userId] });
  }

  async function add() {
    if (!userId || !url.trim()) return;
    setBusy(true);
    try {
      const data = await preview({ data: { url: url.trim() } });
      const { error } = await supabase.from("showcase_posts").insert({
        user_id: userId,
        url: data.url,
        platform: data.platform,
        title: data.title,
        caption: data.caption,
        thumbnail_url: data.thumbnail_url,
        views: data.views,
        likes: data.likes,
        sort_order: (posts?.length ?? 0) + 1,
      });
      if (error) throw error;
      setUrl("");
      await refresh();
      toast.success(t("profiles.postAddedToShowcase"));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("profiles.couldNotReadPost"));
    } finally {
      setBusy(false);
    }
  }

  async function update(post: ShowcasePost, patch: Partial<ShowcasePost>) {
    await supabase.from("showcase_posts").update(patch).eq("id", post.id);
    await refresh();
  }

  async function remove(post: ShowcasePost) {
    await supabase.from("showcase_posts").delete().eq("id", post.id);
    await refresh();
  }

  return (
    <section className="card-surface space-y-5 p-6">
      <div>
        <h2 className="font-display text-xl font-bold">{t("profiles.showcasePastPosts")}</h2>
        <p className="text-sm text-muted-foreground">{t("profiles.showcaseHelperText")}</p>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="flex-1">
          <Label htmlFor="post-url" className="sr-only">
            {t("profiles.postLink")}
          </Label>
          <Input
            id="post-url"
            value={url}
            placeholder="https://www.tiktok.com/@you/video/123456"
            onChange={(e) => setUrl(e.target.value)}
          />
        </div>
        <Button variant="hot" disabled={busy || !url.trim()} onClick={() => void add()}>
          <ImagePlus className="size-4" /> {busy ? t("profiles.reading") : t("profiles.addPost")}
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {(posts ?? []).map((p) => (
          <div key={p.id} className="overflow-hidden rounded-xl border border-border">
            {p.thumbnail_url && (
              <img
                src={p.thumbnail_url}
                alt={p.title ?? "Showcase post"}
                loading="lazy"
                className="h-40 w-full object-cover"
              />
            )}
            <div className="space-y-2 p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate font-display font-semibold">
                    {p.title ?? SOCIAL_LABELS[p.platform] ?? p.platform}
                  </p>
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block truncate text-xs text-muted-foreground underline-offset-2 hover:underline"
                  >
                    {p.url}
                  </a>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={t("profiles.remove")}
                  onClick={() => void remove(p)}
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
              <Input
                defaultValue={p.caption ?? ""}
                placeholder={t("profiles.addShortCaption")}
                onBlur={(e) => void update(p, { caption: e.target.value.slice(0, 300) })}
              />
              <div className="flex gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <Eye className="size-4 text-signal" /> {formatCount(p.views)}
                </span>
                <span className="flex items-center gap-1.5">
                  <Heart className="size-4 text-primary" /> {formatCount(p.likes)}
                </span>
              </div>
            </div>
          </div>
        ))}
        {!posts?.length && (
          <p className="text-sm text-muted-foreground">{t("profiles.noShowcasePostsYet")}</p>
        )}
      </div>
    </section>
  );
}
